import type { BadgeDto, ConversationDto, MeDto, MessageDto, PresenceDto, PublicConfigDto } from '@dijitalapp/shared';
import { byActivity } from './chatList';

/*
 * Reading without a connection, like a native messenger: the last good copy of the account, the app's
 * settings, the chat list and the latest messages of recent chats is kept on this device (IndexedDB), so
 * the app opens and chats can be read with no internet. When the connection is back, everything is
 * refreshed from the server and only what is new is fetched.
 *
 * Privacy: locked chats are never stored; signing out (or a revoked session) wipes the copy. Encrypted
 * messages are kept as they arrive (still encrypted); they are decrypted on screen as usual — decrypted text,
 * previews and keys are never written here (they live in memory only, see lib/e2ee).
 *
 * Besides the chats, what the list draws around them is kept too, so the first frame is the finished one:
 * people's badges and their last known status (shown as not online until the server says otherwise).
 */

const DB_NAME = 'dijitalapp-cache';
const STORE = 'kv';
const MAX_CHATS = 30;
const MAX_MESSAGES = 80;

export interface BootCache {
  config: PublicConfigDto;
  me: MeDto;
  savedAt: string;
}

export interface ChatsCache {
  conversations: ConversationDto[];
  messages: Record<string, MessageDto[]>;
  hasMore: Record<string, boolean>;
  /** Server time of the last full catch-up: edits and deletions since then are fetched on reconnect. */
  syncedAt: string | null;
  savedAt: string;
  /** Last known statuses of people in the list (never "online": that is only believed when live). */
  presence?: Record<string, PresenceDto>;
  /** People's badges in the language they were asked in (lib/badges). */
  badges?: { locale: string; badges: Record<string, BadgeDto[]> };
}

/** Statuses that only hold while connected (believed again only from the server). */
const LIVE_STATUSES = new Set<PresenceDto['status']>(['online', 'idle', 'in_call', 'on_call']);

/** A status from the copy on this device: what the person chose stays (a panel status, last seen), being online does not. */
export function restingPresence(p: PresenceDto): PresenceDto {
  if (!p.online && !LIVE_STATUSES.has(p.status)) return p;
  return { ...p, online: false, status: LIVE_STATUSES.has(p.status) ? 'offline' : p.status };
}

let dbPromise: Promise<IDBDatabase | null> | null = null;
function open(): Promise<IDBDatabase | null> {
  dbPromise ??= new Promise((resolve) => {
    try {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

async function get<T>(key: string): Promise<T | null> {
  const db = await open();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const req = db.transaction(STORE, 'readonly').objectStore(STORE).get(key);
      req.onsuccess = () => resolve((req.result as T | undefined) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function put(key: string, value: unknown): Promise<void> {
  const db = await open();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(value, key);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
      tx.onabort = () => resolve();
    } catch {
      resolve();
    }
  });
}

export function saveBoot(config: PublicConfigDto, me: MeDto): Promise<void> {
  return put('boot', { config, me, savedAt: new Date().toISOString() } satisfies BootCache);
}
export function loadBoot(): Promise<BootCache | null> {
  return get<BootCache>('boot');
}

/** What is worth keeping from the chat store: recent unlocked chats and their latest messages. */
export function snapshotChats(state: {
  conversations: ConversationDto[];
  messages: Record<string, MessageDto[] | undefined>;
  hasMore: Record<string, boolean | undefined>;
  syncedAt: string | null;
  presence?: Record<string, PresenceDto | undefined>;
  badges?: ChatsCache['badges'];
}): ChatsCache {
  const conversations = state.conversations.filter((c) => !c.locked);
  const recent = [...conversations]
    .filter((c) => state.messages[c.id]?.length)
    .sort(byActivity)
    .slice(0, MAX_CHATS);
  const messages: Record<string, MessageDto[]> = {};
  const hasMore: Record<string, boolean> = {};
  for (const c of recent) {
    const all = state.messages[c.id] ?? [];
    messages[c.id] = all.slice(-MAX_MESSAGES);
    hasMore[c.id] = all.length > MAX_MESSAGES || Boolean(state.hasMore[c.id]);
  }
  // Statuses only of people in the kept chats (the list's dots and headers).
  const presence: Record<string, PresenceDto> = {};
  for (const c of conversations) {
    const p = c.peer ? state.presence?.[c.peer.id] : undefined;
    if (p) presence[p.userId] = restingPresence(p);
  }
  return { conversations, messages, hasMore, syncedAt: state.syncedAt, savedAt: new Date().toISOString(), presence, ...(state.badges ? { badges: state.badges } : {}) };
}

export function saveChats(userId: string, cache: ChatsCache): Promise<void> {
  return put(`chats:${userId}`, cache);
}
export function loadChats(userId: string): Promise<ChatsCache | null> {
  return get<ChatsCache>(`chats:${userId}`);
}

/** Signing out: nothing of the account stays on the device. */
export async function wipeOfflineCache(): Promise<void> {
  try {
    localStorage.removeItem('dijitalapp-announcements');
  } catch {
    /* storage unavailable */
  }
  const db = await open();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}
